import axios from "axios";
import store from "../../../redux/store";
import { getWatchlistSummary, getWatchLists } from "../../../redux/new-insight/actions";
import { parseWatchlistCoins } from "../../../shared/other-coins-signal/watchlistCoins";
import {
  getLiveSuggestion,
  liveSuggestionFor,
} from "../../../redux/live-suggestion/liveSuggestionSlice";
import styles from "../../../styles/Components/trading-assistant/onDemandReport.module.css";

const GUIDE_API =
  "https://hav8w1xk8h.execute-api.ap-southeast-2.amazonaws.com/dev/api";
const INSIGHT_API =
  "https://r7bphhzu5c.execute-api.us-east-1.amazonaws.com/dev/api";

const ONE_COIN_MARKETS = [
  { id: "crypto", label: "Crypto" },
  { id: "forex", label: "Forex" },
];

const WATCHLIST_MARKETS = [
  { id: "crypto", label: "Crypto" },
  { id: "forex", label: "Forex" },
];

const PIVOT_FRAMES = [
  { id: "1h", label: "1-hour" },
  { id: "1d", label: "Daily" },
  { id: "1w", label: "Weekly" },
];

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function coinKey(symbol) {
  return String(symbol || "")
    .split("/")[0]
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

function readBody(payload) {
  let data = payload || {};
  if (typeof data.body === "string") {
    try {
      data = JSON.parse(data.body);
    } catch (error) {
      data = payload || {};
    }
  }
  return data;
}

function percent(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number === 0) return "";
  return `${number.toPrecision(2)}%`;
}

function framePercent(item, frame, key) {
  const fromFrame = item?.MT_SLTP?.[frame]?.[key];
  const fallback = frame === "1h" ? item?.[key] : undefined;
  return percent(fromFrame ?? fallback);
}

function callOf(item) {
  const side = String(item?.TRADE_TYPE || "").split("/")[0];
  const category = item?.CATEGORY_INFO?.category || "";
  const score = Number(item?.SIGNAL_STRENGTH);
  const word =
    side === "Buy" || side === "Sell"
      ? Number.isFinite(score) && score * 20 >= 50
        ? "Strong"
        : "Weak"
      : "";
  const raw = Number(item?.SUGGESTED_AMOUNT_PERCENTAGE);
  const size = Number.isFinite(raw) ? Math.round(raw * 1000) / 10 : null;
  return {
    side: side || "",
    category,
    word,
    size: side === "Buy" || side === "Sell" ? size : null,
    stopHour: framePercent(item, "1h", "STOP_LOSS_PERCENT"),
    targetHour: framePercent(item, "1h", "TAKE_PROFIT_PERCENT"),
    stopDay: framePercent(item, "1d", "STOP_LOSS_PERCENT"),
    targetDay: framePercent(item, "1d", "TAKE_PROFIT_PERCENT"),
    stopWeek: framePercent(item, "1w", "STOP_LOSS_PERCENT"),
    targetWeek: framePercent(item, "1w", "TAKE_PROFIT_PERCENT"),
  };
}

function findCall(list, symbol) {
  const key = coinKey(symbol);
  const item = (list || []).find((row) => coinKey(row?.SYMBOL_NAME) === key);
  return item ? callOf(item) : null;
}

function fxName(symbol) {
  const key = coinKey(symbol);
  if (key === "XAU") return "XAUUSD";
  if (key === "XAG") return "XAGUSD";
  return key;
}

function forexLookup(symbol) {
  const key = coinKey(symbol);
  if (key === "XAU" || key === "XAUT" || key === "PAXG") return "XAUUSD";
  if (key === "XAG") return "XAGUSD";
  return key;
}

function isFxPair(symbol) {
  const key = coinKey(symbol);
  if (key === "XAUUSD" || key === "XAGUSD") return true;
  return key.length === 6 && /^[A-Z]{6}$/.test(key) && key !== "RENDER";
}

async function readOneSymbol(email, user, market, symbol) {
  const response = await axios.post(
    GUIDE_API,
    {
      platform_mode: "trade_assistant",
      email,
      user,
      request_type: "get_trade_suggestion",
      portfolio_type: "primary",
      market_type: market,
      other_coins_to_analyse: [symbol],
    },
    { timeout: 25000 },
  );
  const data = readBody(response?.data);
  const list = data?.other_coins_analyse || data?.ASSISTANT_SUGGESTIONS;
  return Array.isArray(list) ? list : null;
}

async function readSavedBoard(email, user, market) {
  const response = await axios.post(
    GUIDE_API,
    {
      platform_mode: "trade_assistant",
      email,
      user,
      request_type: "get_trade_suggestion",
      portfolio_type: "primary",
      market_type: market,
    },
    { timeout: 20000 },
  );
  const data = readBody(response?.data);
  if (data?.status === "miss" || !Array.isArray(data?.ASSISTANT_SUGGESTIONS)) {
    return null;
  }
  return data.ASSISTANT_SUGGESTIONS;
}

function pivotSide(live) {
  const side = String(live?.prediction || "").split("/")[0];
  if (!side) return "";
  return side;
}

function pivotMiss(live) {
  if (live?.miss === "no_weekly_model") return "no weekly model yet";
  if (live?.miss === "not_enough_weeks") return "not enough weekly history";
  return "";
}

async function ensurePivot(dispatch, ids, symbol, frame) {
  const ready = () =>
    liveSuggestionFor(store.getState()?.liveSuggestion?.suggestions, symbol, frame);
  const existing = ready();
  if (pivotSide(existing)) return { side: pivotSide(existing), miss: "" };
  if (pivotMiss(existing)) return { side: "", miss: pivotMiss(existing) };
  dispatch(
    getLiveSuggestion(
      ids.cognito,
      ids.user,
      "kucoin",
      symbol,
      false,
      null,
      false,
      false,
      0,
      [],
      frame,
    ),
  );
  const started = Date.now();
  const waitMs = frame === "1w" ? 25000 : 14000;
  while (Date.now() - started < waitMs) {
    await sleep(500);
    const live = ready();
    if (pivotSide(live)) return { side: pivotSide(live), miss: "" };
    if (pivotMiss(live)) return { side: "", miss: pivotMiss(live) };
  }
  const live = ready();
  return { side: pivotSide(live), miss: pivotMiss(live) };
}

function sizeText(size) {
  if (!Number.isFinite(size)) return "—";
  return `${Number.isInteger(size) ? size : size.toFixed(1)}%`;
}

function tone(side) {
  const value = String(side || "").toLowerCase();
  if (value === "buy") return styles.buy;
  if (value === "sell") return styles.sell;
  return styles.hold;
}

function marketLine(label, board, symbol) {
  if (!board) return "";
  const lookup = label === "Forex" ? forexLookup(symbol) : symbol;
  const call = findCall(board, lookup);
  if (!call?.side) return "";
  const alias =
    label === "Forex" && coinKey(lookup) !== coinKey(symbol) ? `${lookup} ` : "";
  return `${label}: ${alias}${call.word ? `${call.word} ` : ""}${call.side}`;
}

function rankedCalls(list) {
  return (list || [])
    .map((row) => {
      const score = Number(row?.SIGNAL_STRENGTH);
      return {
        symbol: coinKey(row?.SYMBOL_NAME),
        strength: Number.isFinite(score) ? score : 0,
        side: String(row?.TRADE_TYPE || "").split("/")[0],
      };
    })
    .filter((item) => item.symbol && (item.side === "Buy" || item.side === "Sell"))
    .sort((a, b) => b.strength - a.strength || (a.side === "Buy" ? -1 : 1));
}

function buildCard(symbol, reason, cryptoBoard, pivots, otherBoards, home) {
  const crypto = findCall(cryptoBoard, symbol);
  const homeCall = home
    ? findCall(home.board, home.label === "Forex" ? forexLookup(symbol) : symbol)
    : null;
  const lead =
    home?.label && home.label !== "Crypto" && homeCall?.side ? homeCall : crypto?.side ? crypto : homeCall;
  const side = lead?.side || "";
  const daily = pivots["1d"] || "";
  const week = pivots["1w"] || "";
  const disagrees = Boolean(side && daily && daily !== "Hold" && daily !== side);
  let action = side === "Buy" ? "Add" : side === "Sell" ? "Skip" : "Leave";
  const markets = [
    marketLine("Crypto", cryptoBoard, symbol),
    ...otherBoards.map((item) => marketLine(item.label, item.board, symbol)),
  ].filter(Boolean);
  const forex = markets.find((item) => item.startsWith("Forex:"));
  const forexSide = forex && !forex.endsWith("not listed") && !forex.endsWith("did not load")
    ? forex.split(" ").pop()
    : "";
  let line = isFxPair(symbol)
    ? `${symbol} is a forex pair. Forex has no weekly pivot.`
    : `${symbol} has no saved crypto call.`;
  if (side === "Hold") line = `${symbol} stays off the book. The category call is Hold.`;
  else if (side && disagrees) {
    line = `${symbol}: follow the ${side}. The daily pivot disagrees, so do not add it from the pivot.`;
  } else if (side) {
    const where = home?.label && home.label !== "Crypto" ? ` on ${home.label}` : "";
    line = week
      ? `${symbol}: follow the ${side}${where}. The weekly pivot is ${week}.`
      : `${symbol}: follow the ${side}${where}.`;
  } else if (forexSide === "Buy" || forexSide === "Sell" || forexSide === "Hold") {
    action = forexSide === "Buy" ? "Add" : forexSide === "Sell" ? "Skip" : "Leave";
    line = `${symbol}: ${forex}. Forex has no weekly pivot.`;
  }
  return {
    symbol,
    reason,
    action,
    side,
    word: lead?.word || "",
    category: lead?.category || "",
    size: lead?.size,
    hour: pivots["1h"] || "",
    daily,
    week,
    weekNote: pivots.weekNote || "",
    checkedWeek: Boolean(pivots.checked),
    markets,
    stopHour: crypto?.stopHour || "",
    targetHour: crypto?.targetHour || "",
    stopDay: crypto?.stopDay || "",
    targetDay: crypto?.targetDay || "",
    stopWeek: crypto?.stopWeek || "",
    targetWeek: crypto?.targetWeek || "",
    others: markets,
    context: [],
    line,
    disagrees,
  };
}

function levelLine(stop, target) {
  if (!stop && !target) return "";
  return `${stop ? `Stop ${stop}` : "No stop"}${target ? ` · Target ${target}` : ""}`;
}

function pivotRow(label, side, stop, target) {
  if (!side) return null;
  const levels = levelLine(stop, target);
  return (
    <p>
      {label} <span className={tone(side)}>{side}</span>
      {levels ? ` · ${levels}` : ""}
    </p>
  );
}

function CoinCard({ card }) {
  const forexLine = (card.markets || []).find((line) => String(line).startsWith("Forex:"));
  const call = card.side
    ? `${card.category ? `${card.category} · ` : ""}${card.word ? `${card.word} ` : ""}${card.side}`
    : forexLine || "No saved call";
  return (
    <article className={styles.coin}>
      <header>
        <strong>{card.symbol}</strong>
        <span className={card.action === "Add" ? styles.buy : card.action === "Skip" ? styles.sell : styles.hold}>
          {card.action}
        </span>
      </header>
      {card.reason ? <p className={styles.reason}>{card.reason}</p> : null}
      <p className={tone(card.side)}>{call}{card.action === "Add" ? ` · ${sizeText(card.size)}` : ""}</p>
      {pivotRow("1-hour pivot", card.hour, card.stopHour, card.targetHour)}
      {pivotRow("Daily pivot", card.daily, card.stopDay, card.targetDay)}
      {card.checkedWeek
        ? pivotRow("Weekly pivot", card.week, card.stopWeek, card.targetWeek) || (
            <p>Weekly pivot {card.weekNote || "is not back yet"}</p>
          )
        : null}
      {(card.markets || []).length ? (
        <p className={styles.others}>{(card.markets || []).join(" · ")}</p>
      ) : null}
      {(card.context || []).length ? (
        <p className={styles.others}>{(card.context || []).join(" · ")}</p>
      ) : null}
      <p className={styles.note}>{card.line}</p>
    </article>
  );
}

export function DeepCards({ cards }) {
  if (cards.length === 1) {
    return (
      <div className={styles.groups}>
        <CoinCard card={cards[0]} />
      </div>
    );
  }
  const add = cards.filter((card) => card.action === "Add");
  const skip = cards.filter((card) => card.action === "Skip");
  const leave = cards.filter((card) => card.action === "Leave").map((card) => card.symbol);
  return (
    <div className={styles.groups}>
      {add.length ? (
        <section>
          <h4>Add</h4>
          {add.map((card) => (
            <CoinCard key={card.symbol} card={card} />
          ))}
        </section>
      ) : null}
      {skip.length ? (
        <section>
          <h4>Skip</h4>
          {skip.map((card) => (
            <CoinCard key={card.symbol} card={card} />
          ))}
        </section>
      ) : null}
      {leave.length ? <p className={styles.note}>Leave alone: {leave.join(", ")}.</p> : null}
    </div>
  );
}

function boardHit(boards, symbol) {
  for (const market of ONE_COIN_MARKETS) {
    const list = boards[market.id];
    if (!list) continue;
    const lookup = market.id === "forex" ? forexLookup(symbol) : symbol;
    const call = findCall(list, lookup);
    if (call?.side === "Buy" || call?.side === "Sell") {
      return { label: market.label, board: list };
    }
  }
  return null;
}

function listedAnywhere(boards, symbol) {
  return ONE_COIN_MARKETS.some((market) => {
    const list = boards[market.id];
    if (!list) return false;
    const lookup = market.id === "forex" ? forexLookup(symbol) : symbol;
    return Boolean(findCall(list, lookup)?.side);
  });
}

async function personalSymbols(dispatch, user, email) {
  let insight = store.getState()?.newInsightState || {};
  if (!insight.watchlistsFetched && insight.watchlistStatus !== "loading") {
    await dispatch(getWatchLists(user, email));
    insight = store.getState()?.newInsightState || {};
  }
  const started = Date.now();
  while (insight.watchlistStatus === "loading" && Date.now() - started < 8000) {
    await sleep(300);
    insight = store.getState()?.newInsightState || {};
  }
  return [
    ...new Set(
      parseWatchlistCoins(insight.watchlists?.[0]?.list_of_coins)
        .map((item) => coinKey(item))
        .filter(Boolean),
    ),
  ];
}

function otherBoardsFor(markets, boards) {
  return markets
    .filter((market) => market.id !== "crypto")
    .map((market) => ({ label: market.label, board: boards[market.id] }));
}

export async function runDeepCheck(dispatch, mode, onStatus, named) {
  const note = typeof onStatus === "function" ? onStatus : () => {};
  const userData = store.getState()?.main?.userData || {};
  const email = userData["cognito:username"];
  const user = userData["custom:custom_username"];
  if (!email || !user) {
    return { text: "Sign in again, then try this check.", cards: [] };
  }
  const weekly = mode === "weekly";
  const namedSymbol = fxName(named);
  const scan = !weekly && !namedSymbol;
  let coins = [];
  const reasons = {};
  const homes = {};
  if (weekly) {
    note("Updating the weekly list…");
    await dispatch(getWatchlistSummary(user, email, true));
    let insight = store.getState()?.newInsightState || {};
    const waitStarted = Date.now();
    while (
      (insight.watchlistSummaryStatus === "loading" ||
        insight.watchlistSummaryStatus === "replacing") &&
      Date.now() - waitStarted < 20000
    ) {
      await sleep(300);
      insight = store.getState()?.newInsightState || {};
    }
    if (insight.watchlistSummaryStatus === "error" || !insight.watchlistSummary?.replaced) {
      return { text: "The weekly list is unchanged. Try again in a moment.", cards: [] };
    }
    const suggested = Array.isArray(insight.watchlistSummary?.suggested_coins)
      ? insight.watchlistSummary.suggested_coins
      : [];
    coins = suggested
      .map((item) => {
        const symbol = coinKey(item?.symbol);
        if (symbol) reasons[symbol] = String(item?.reason || "").trim();
        return symbol;
      })
      .filter(Boolean)
      .slice(0, 4);
    if (!coins.length) {
      return { text: "The weekly list has no coins to check yet.", cards: [] };
    }
  } else if (namedSymbol) {
    coins = [namedSymbol];
  }

  const markets = weekly ? WATCHLIST_MARKETS : ONE_COIN_MARKETS;
  const boards = {};
  for (const market of markets) {
    note(`Reading ${market.label.toLowerCase()}…`);
    try {
      boards[market.id] = await readSavedBoard(email, user, market.id);
    } catch (error) {
      boards[market.id] = null;
    }
  }
  const openMarket = store.getState()?.tradingAssistant?.aiMarketType || "crypto";
  const openList = store.getState()?.tradingAssistant?.tradeAssistant?.ASSISTANT_SUGGESTIONS;
  if (Array.isArray(openList) && openList.length) boards[openMarket] = openList;

  if (!weekly && namedSymbol && isFxPair(namedSymbol) && !findCall(boards.forex, forexLookup(namedSymbol))) {
    note("Reading the 1-hour forex call…");
    try {
      const one = await readOneSymbol(email, user, "forex", namedSymbol);
      if (one) boards.forex = (boards.forex || []).concat(one);
    } catch (error) {
      boards.forex = boards.forex || null;
    }
  }

  const quiet = [];
  if (scan) {
    ONE_COIN_MARKETS.forEach((market) => {
      const list = boards[market.id];
      if (!list) {
        quiet.push(`${market.label} did not load.`);
        return;
      }
      const best = rankedCalls(list);
      if (!best.length) {
        quiet.push(`${market.label} has no Buy or Sell.`);
        return;
      }
      let taken = 0;
      best.forEach((item) => {
        if (taken >= 2 || coins.includes(item.symbol)) return;
        taken += 1;
        coins.push(item.symbol);
        homes[item.symbol] = { label: market.label, board: list };
        reasons[item.symbol] = market.label;
      });
    });
    note("Reading your list…");
    const saved = await personalSymbols(dispatch, user, userData.email || email);
    let fetches = 0;
    for (const symbol of saved) {
      if (coins.includes(symbol)) {
        reasons[symbol] = "Your list";
        continue;
      }
      const hit = boardHit(boards, symbol);
      if (hit) {
        coins.push(symbol);
        homes[symbol] = { label: hit.label, board: hit.board };
        reasons[symbol] = "Your list";
        continue;
      }
      if (listedAnywhere(boards, symbol) || fetches >= 2) continue;
      fetches += 1;
      const marketId = isFxPair(symbol) ? "forex" : "crypto";
      note(`Reading ${symbol} from your list…`);
      try {
        const one = await readOneSymbol(email, user, marketId, symbol);
        const row = (one || []).find((item) => coinKey(item?.SYMBOL_NAME) === symbol);
        const side = String(row?.TRADE_TYPE || "").split("/")[0];
        if (!row) {
          quiet.push(`${symbol} on your list did not load.`);
          continue;
        }
        if (side !== "Buy" && side !== "Sell") continue;
        const label = marketId === "forex" ? "Forex" : "Crypto";
        boards[marketId] = (boards[marketId] || []).concat(one);
        coins.push(symbol);
        homes[symbol] = { label, board: boards[marketId] };
        reasons[symbol] = "Your list";
      } catch (error) {
        quiet.push(`${symbol} on your list did not load.`);
      }
    }
    if (!coins.length) {
      return {
        text: quiet.join(" ") || "No Buy or Sell is on crypto, forex, or stock.",
        cards: [],
      };
    }
  }

  const ids = { cognito: email, user };
  const cards = [];
  const extras = otherBoardsFor(markets, boards);
  for (const coin of coins) {
    const pivots = { "1h": "", "1d": "", "1w": "", weekNote: "", checked: false };
    if (!scan && !isFxPair(coin) && findCall(boards.crypto, coin)?.side) {
      pivots.checked = true;
      for (const frame of PIVOT_FRAMES) {
        note(`Checking the ${frame.label.toLowerCase()} pivot for ${coin}…`);
        try {
          const result = await ensurePivot(dispatch, ids, coin, frame.id);
          pivots[frame.id] = result?.side || "";
          if (frame.id === "1w") pivots.weekNote = result?.miss || "";
        } catch (error) {
          pivots[frame.id] = "";
        }
      }
    }
    let home = homes[coin];
    if (!scan && !weekly && !home && !findCall(boards.crypto, coin)?.side) {
      home = extras.find((item) =>
        findCall(item.board, item.label === "Forex" ? forexLookup(coin) : coin)?.side,
      );
    }
    cards.push(
      buildCard(coin, reasons[coin] || "", boards.crypto, pivots, extras, home),
    );
  }
  if (scan && cards.length === 1) {
    quiet.push("This scan only found one call. Name another coin, such as analyse BTC.");
  }
  const names = coins.join(", ");
  let text = weekly
    ? `Weekly picks: ${names}. A longer hold follows the category call.`
    : scan
      ? `Best calls on crypto, forex, and stock: ${names}. A longer hold follows each market's category call.`
      : `${names} across crypto, forex, and stock. A longer hold follows the category call.`;
  if (quiet.length) text = `${text} ${quiet.join(" ")}`;
  let fedLine = "";
  if (!scan && !weekly && cards.length === 1) {
    note("Reading sentiment and the Fed watch…");
    const [sentiment, fed] = await Promise.all([
      readCoinContext(email, user, cards[0].symbol),
      readFedLean(),
    ]);
    cards[0].context = [sentiment, fed].filter(Boolean);
  } else if (scan) {
    note("Reading the Fed watch…");
    fedLine = await readFedLean();
    if (fedLine) text = `${text} ${fedLine}`;
  }
  note("Writing this in plain language…");
  const spoken = await humanSummary(
    [cards.map(cardFacts).join("\n"), quiet.join(" "), fedLine].filter(Boolean).join("\n"),
  );
  if (spoken) {
    const kept = quiet.filter((line) => !spoken.includes(line.replace(/\.$/, "")));
    const fedKept = fedLine && !spoken.toLowerCase().includes("fed") ? fedLine : "";
    text = [spoken, ...kept, fedKept].filter(Boolean).join(" ");
  }
  return { text, cards };
}

function cardFacts(card) {
  const bits = [card.symbol];
  if (card.side) bits.push(`category call ${card.word ? `${card.word} ` : ""}${card.side}`);
  if (card.hour) bits.push(`1-hour pivot ${card.hour}`);
  if (card.daily) bits.push(`daily pivot ${card.daily}`);
  if (card.checkedWeek) {
    bits.push(card.week ? `weekly pivot ${card.week}` : `weekly pivot ${card.weekNote || "is not back yet"}`);
  }
  (card.markets || []).forEach((item) => bits.push(item));
  (card.context || []).forEach((item) => bits.push(item));
  if (isFxPair(card.symbol)) bits.push("forex has no weekly pivot");
  if (card.line) bits.push(card.line);
  return bits.join(". ");
}

async function readCoinContext(email, user, symbol) {
  try {
    const response = await axios.post(
      INSIGHT_API,
      {
        request_type: "get_watchlist_summary",
        platform_mode: "insight_platform",
        user,
        email,
        symbol,
      },
      { timeout: 12000 },
    );
    const row = readBody(response?.data)?.coin_context;
    if (!row?.sentiment_label && !row?.geopolitical_risk_label) return "";
    const score = Number(row.sentiment_score);
    const scoreText = Number.isFinite(score) ? ` ${score.toFixed(2)}` : "";
    const risk = row.geopolitical_risk_label
      ? `, ${row.geopolitical_risk_label} geopolitical risk`
      : "";
    return `Sentiment ${row.sentiment_label || "unknown"}${scoreText}${risk}`;
  } catch (error) {
    return "";
  }
}

async function readFedLean() {
  try {
    const response = await axios.post(
      GUIDE_API,
      {
        platform_mode: "trade_assistant",
        request_type: "market_context",
      },
      { timeout: 8000 },
    );
    const lean = String(readBody(response?.data)?.fed || "").toLowerCase();
    if (lean !== "cut" && lean !== "hold" && lean !== "hike") return "";
    return `Fed watch leans ${lean}`;
  } catch (error) {
    return "";
  }
}

async function humanSummary(facts) {
  if (!facts) return "";
  try {
    const response = await axios.post(
      GUIDE_API,
      {
        platform_mode: "guide_chat",
        summary_only: "True",
        question: "Explain this check.",
        signals: facts,
        page: "assistant",
      },
      { timeout: 12000 },
    );
    const data = readBody(response?.data);
    return String(data?.answer || "").trim();
  } catch (error) {
    return "";
  }
}
